import { jwtDecode } from 'jwt-decode';
import {
  appActive,
  appOpened,
  appInBackground,
  startListening,
  appSync,
  appLogout,
  isAnyOf,
  registerUser,
  initiateLogin,
  setFirstName,
  displayLastLaunchedGameToast,
} from '@bonanza-social/toolkit/state';
import { apiConfig } from '@bonanza-social/toolkit/api';
import { AsyncStorage, SecureStorage } from '@bonanza-social/toolkit/storage';
import { logError, logInfo } from '@bonanza-social/toolkit/logging';
import { navigationRef } from '@bonanza-social/navigation';
import {
  clearMFAResendCodeTimeout,
  getRefreshTokenPromise,
  getToken,
  login,
  logout,
  refreshToken,
  resendMFACode,
  resetMFAResendCodeTimeout,
  resetPassword,
  selectIsLogged,
  selectMFAResendCodeTimeout,
  selectRefreshTokenDataState,
  setExistingUserName,
  setIsExistingUser,
  setLoggedIn,
  setRefreshTokenDataState,
  validateMFA,
} from './auth.slice';
import AuthService from '../../services/AuthService';
import {
  setAutoLoginFulfilled,
  setAutoLoginPending,
  setAutoLoginRejected,
} from '@bonanza-social/registration/state';
import { verifyBiometrics } from '../biometrics/biometrics.slice';
import { Platform } from 'react-native';
import DeviceInfo from 'react-native-device-info';

startListening({
  actionCreator: initiateLogin,
  effect: () => {
    navigationRef.navigate('LoginScreen');
  },
});

startListening({
  matcher: isAnyOf(
    login.fulfilled,
    validateMFA.fulfilled,
    verifyBiometrics.fulfilled,
  ),
  effect: async (action, { dispatch }) => {
    const { meta } = action as
      | ReturnType<typeof login.fulfilled>
      | ReturnType<typeof validateMFA.fulfilled>
      | ReturnType<typeof verifyBiometrics.fulfilled>;

    await AsyncStorage.setItem(
      'eligible_to_play',
      `${Boolean(meta.arg && 'isEligible' in meta.arg && meta.arg.isEligible)}`,
    );

    dispatch(appSync());
    dispatch(setIsExistingUser(true));

    const userId = await apiConfig.getUserId();

    if (userId) {
      AuthService.loginCompleted({ pathParams: { userId } });
    }
    dispatch(displayLastLaunchedGameToast());
  },
});

startListening({
  actionCreator: login.rejected,
  effect: (action, { dispatch }) => {
    if (action.payload && 'requireTwoFactorVerification' in action.payload) {
      dispatch(resetMFAResendCodeTimeout());
    }
  },
});

startListening({
  actionCreator: resendMFACode.fulfilled,
  effect: (_, { dispatch }) => {
    dispatch(resetMFAResendCodeTimeout());
  },
});

startListening({
  actionCreator: resetMFAResendCodeTimeout,
  effect: async (_, { getState, dispatch, cancelActiveListeners, delay }) => {
    cancelActiveListeners();

    const state = getState();
    const timeout = selectMFAResendCodeTimeout(state);

    if (!timeout) {
      return;
    }

    const timeLeft = Math.max(timeout - +new Date(), 0);

    await delay(timeLeft);

    dispatch(clearMFAResendCodeTimeout());
  },
});

startListening({
  matcher: isAnyOf(
    appOpened,
    appActive,
    refreshToken.fulfilled,
    login.fulfilled,
    validateMFA.fulfilled,
    verifyBiometrics.fulfilled,
  ),
  effect: async (_, { dispatch, cancelActiveListeners, delay, take }) => {
    cancelActiveListeners();

    const token = await apiConfig.getToken();

    if (token) {
      const idToken = jwtDecode(token.access_token);
      const expirySeconds = idToken.exp || 0;
      const timeLeft = expirySeconds * 1000 - +new Date();
      const timeout = Math.max(timeLeft - 10000, 0);

      logInfo('auth', 'SETTING REFRESH TIMER FOR SECONDS: ', timeout / 1000);

      const result = await Promise.race([
        delay(timeout),
        take(appInBackground.match),
      ]);

      if (Array.isArray(result) && result[0]?.type === appInBackground.type) {
        logInfo(
          'auth',
          'Refresh token timer cancelled due to app going to background',
        );
        return;
      }

      await delay(timeout);

      dispatch(refreshToken());
    }
  },
});

startListening({
  actionCreator: appInBackground,
  effect: (_, { getState, dispatch }) => {
    const isLogged = selectIsLogged(getState());

    if (isLogged) {
      dispatch(setRefreshTokenDataState('stale'));
    }
  },
});

startListening({
  matcher: isAnyOf(appOpened, appActive),
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const isLogged = selectIsLogged(state);
    const refreshTokenDataState = selectRefreshTokenDataState(state);
    const isLoggedInOnOpen = action.type === appOpened.type && isLogged;

    if (isLoggedInOnOpen || refreshTokenDataState === 'stale') {
      const tokenPromise = getRefreshTokenPromise();

      if (tokenPromise) {
        await tokenPromise;

        dispatch(appSync());

        return;
      }

      const result = await dispatch(refreshToken());

      if (refreshToken.fulfilled.match(result)) {
        dispatch(appSync());
      }
    }
  },
});

startListening({
  actionCreator: resetPassword.fulfilled,
  effect: () => {
    if (Platform.OS === 'android') {
      navigationRef.reset({
        index: 1,
        routes: [{ name: 'IntroScreen' }, { name: 'LoginScreen' }],
      });
    }
  },
});

startListening({
  actionCreator: registerUser,
  effect: async (action, { dispatch }) => {
    const { codeVerifier, authorizationCode } = action.payload;
    dispatch(setAutoLoginPending());

    try {
      await getToken({
        grant_type: 'authorization_code',
        redirect_uri: 'bonanzasocial://callback',
        code: authorizationCode,
        code_verifier: codeVerifier,
      });
      dispatch(setLoggedIn(true));
      dispatch(appSync());

      const userId = await apiConfig.getUserId();
      if (userId) {
        await AuthService.loginCompleted({ pathParams: { userId } });
      }
      dispatch(setAutoLoginFulfilled());
      dispatch(setIsExistingUser(true));
    } catch (error) {
      logError('Registration token exchange failed:', error);
      dispatch(setAutoLoginRejected());
      dispatch(setIsExistingUser(false));
    }
  },
});

startListening({
  actionCreator: logout.fulfilled,
  effect: (_, { dispatch }) => {
    dispatch(appLogout());
  },
});

startListening({
  actionCreator: refreshToken.rejected,
  effect: (_, { dispatch }) => {
    dispatch(logout());

    SecureStorage.removeItem('token');
    SecureStorage.removeItem('access_token');
    SecureStorage.removeItem('user_id');
    SecureStorage.removeItem('refresh_token');
  },
});

let heartBeatInterval: number | undefined;

const clearHeartBeatInterval = () => {
  clearInterval(heartBeatInterval);
  heartBeatInterval = undefined;
};

startListening({
  actionCreator: appSync,
  effect: () => {
    clearHeartBeatInterval();

    heartBeatInterval = window.setInterval(async () => {
      logInfo('Activity', 'check');

      const data = {
        playerId: await apiConfig.getUserId(),
        pageUrl: navigationRef.getCurrentRoute()?.name || 'Home',
        userAgent: await DeviceInfo.getUserAgent(),
        visitedAt: new Date().toISOString(),
      };

      AuthService.siteLocation({ data });
    }, 15000);
  },
});

startListening({
  actionCreator: setFirstName,
  effect: (action, { dispatch }) => {
    const { firstName } = action.payload;
    dispatch(setExistingUserName(firstName));
  },
});

startListening({
  matcher: isAnyOf(appInBackground, logout.fulfilled),
  effect: () => {
    clearHeartBeatInterval();
  },
});
